import { OysterIcon } from "@oysterhr/icons"
import classNames from "classnames"
import { isEmpty, isString } from "lodash-es"
import { cloneElement, forwardRef, isValidElement, ReactElement, useMemo, useState } from "react"
import { Icon } from "../Icon"
import { AvatarIndicator } from "./AvatarIndicator"
import {
  AVATAR_CLASSNAME_BY_SIZE,
  AVATAR_DEFAULT_CLASSNAME,
  PLACEHOLDER_ICON_SIZE,
} from "./constants"
import { AvatarProps } from "./types"
import { getInitials } from "./utils"

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ indicator: indicatorProp, name, size = "xs", src, iconName = "User", ...rest }, ref) => {
    const ariaLabel = name ? `${name} avatar` : "Avatar"
    const indicator = useMemo(() => {
      if (!indicatorProp) return null
      return isValidElement(indicatorProp) && cloneElement(indicatorProp as ReactElement, { size })
    }, [indicatorProp, size])
    const [imageError, setImageError] = useState<boolean>(false)
    const isUsingSrc = !imageError && isString(src) && !isEmpty(src)

    return (
      <div
        className={classNames(AVATAR_DEFAULT_CLASSNAME, AVATAR_CLASSNAME_BY_SIZE[size], {
          "border border-primary-base": !isUsingSrc,
        })}
        ref={ref}
        {...rest}
      >
        {!isUsingSrc && name && (
          <span aria-label={ariaLabel} role="img">
            {getInitials(name)}
          </span>
        )}
        {!isUsingSrc && !name && (
          <Icon
            name={iconName as OysterIcon}
            size={PLACEHOLDER_ICON_SIZE[size]}
            aria-label={ariaLabel}
            role="img"
          />
        )}
        {isUsingSrc && (
          <img
            src={src}
            alt={name || undefined}
            className="rounded-full object-center"
            onError={() => setImageError(true)}
          />
        )}
        {indicator}
      </div>
    )
  },
)

const AvatarNamespace = Object.assign(Avatar, { Indicator: AvatarIndicator })

export { AvatarNamespace as Avatar }
